import type {
  ConnectionGroup,
  HealthCheck,
  HealthLevel,
  IndexHealth,
  RouteLatency,
  StatementStats,
  TableHealth,
} from '#shared/types';
import { Badge, type BadgeTone } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { formatCount } from '#client/lib/format';
import { formatBytes, formatMs, formatWhen } from './database-health.logic';

/**
 * The database health tables' columns, at module scope like every other
 * column array in the app.
 */

const LEVEL: Record<HealthLevel, { tone: BadgeTone; label: string }> = {
  ok: { tone: 'success', label: 'OK' },
  warning: { tone: 'warning', label: 'Warning' },
  critical: { tone: 'critical', label: 'Critical' },
};

export const checkColumns: Column<HealthCheck>[] = [
  {
    key: 'level',
    header: 'Status',
    render: (row) => <Badge tone={LEVEL[row.level].tone}>{LEVEL[row.level].label}</Badge>,
    width: '110px',
  },
  { key: 'label', header: 'Check', render: (row) => row.label, width: '190px' },
  { key: 'value', header: 'Now', render: (row) => <span className="tnum">{row.value}</span> },
  { key: 'detail', header: 'What it means', render: (row) => row.detail },
];

export const latencyColumns: Column<RouteLatency>[] = [
  { key: 'route', header: 'Route', render: (row) => <code>{row.route}</code> },
  { key: 'count', header: 'Samples', render: (row) => formatCount(row.count), align: 'end' },
  { key: 'p50', header: 'p50', render: (row) => formatMs(row.p50_ms), align: 'end' },
  { key: 'p95', header: 'p95', render: (row) => formatMs(row.p95_ms), align: 'end' },
  { key: 'p99', header: 'p99', render: (row) => formatMs(row.p99_ms), align: 'end' },
  { key: 'max', header: 'Max', render: (row) => formatMs(row.max_ms), align: 'end' },
];

export const statementColumns: Column<StatementStats>[] = [
  {
    key: 'query',
    header: 'Statement',
    render: (row) => <code className="c-health__query">{row.query}</code>,
  },
  { key: 'calls', header: 'Calls', render: (row) => formatCount(row.calls), align: 'end' },
  { key: 'total', header: 'Total', render: (row) => formatMs(row.total_ms), align: 'end' },
  { key: 'mean', header: 'Mean', render: (row) => formatMs(row.mean_ms), align: 'end' },
  { key: 'stddev', header: 'Std dev', render: (row) => formatMs(row.stddev_ms), align: 'end' },
  {
    key: 'hit',
    header: 'Cache hit',
    render: (row) => (row.hit_pct === null ? '—' : `${row.hit_pct}%`),
    align: 'end',
  },
];

export const tableColumns: Column<TableHealth>[] = [
  { key: 'table', header: 'Table', render: (row) => <code>{row.table}</code> },
  { key: 'rows', header: 'Rows', render: (row) => formatCount(row.live_rows), align: 'end' },
  {
    key: 'dead',
    header: 'Dead',
    render: (row) => `${formatCount(row.dead_rows)} (${row.dead_pct}%)`,
    align: 'end',
  },
  { key: 'size', header: 'Size', render: (row) => formatBytes(row.size_bytes), align: 'end' },
  { key: 'seq', header: 'Seq scans', render: (row) => formatCount(row.seq_scans), align: 'end' },
  {
    key: 'idx',
    header: 'Index scans',
    render: (row) => formatCount(row.index_scans),
    align: 'end',
  },
  { key: 'vacuum', header: 'Last vacuum', render: (row) => formatWhen(row.last_vacuum) },
  { key: 'analyze', header: 'Last analyze', render: (row) => formatWhen(row.last_analyze) },
  {
    key: 'modified',
    header: 'Changed since',
    render: (row) => formatCount(row.modified_since_analyze),
    align: 'end',
  },
];

export const indexColumns: Column<IndexHealth>[] = [
  { key: 'index', header: 'Index', render: (row) => <code>{row.index}</code> },
  { key: 'table', header: 'Table', render: (row) => <code>{row.table}</code> },
  { key: 'size', header: 'Size', render: (row) => formatBytes(row.size_bytes), align: 'end' },
  { key: 'scans', header: 'Scans', render: (row) => formatCount(row.scans), align: 'end' },
  {
    key: 'note',
    header: 'Note',
    render: (row) => <IndexNote index={row} />,
  },
];

export const connectionColumns: Column<ConnectionGroup>[] = [
  { key: 'app', header: 'Application', render: (row) => <code>{row.application}</code> },
  { key: 'state', header: 'State', render: (row) => row.state },
  { key: 'count', header: 'Connections', render: (row) => formatCount(row.count), align: 'end' },
  {
    key: 'oldest',
    header: 'Oldest transaction',
    render: (row) => (row.oldest_xact_seconds === null ? '—' : `${row.oldest_xact_seconds} s`),
    align: 'end',
  },
];

/** Why an index is worth a second look — or nothing, when it is pulling its weight. */
function IndexNote({ index }: { index: IndexHealth }) {
  if (index.covered_by) return <Badge tone="warning">Covered by {index.covered_by}</Badge>;
  if (index.scans === 0 && !index.enforces) {
    return <Badge tone="info">Unused since stats reset</Badge>;
  }
  if (index.enforces) return <Badge tone="neutral">Enforces a constraint</Badge>;
  return null;
}
